import React, { useMemo, useRef, useState } from 'react';
import { Download, FileUp, Loader2, Paperclip, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { inmueblesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const kb = (v) => (v > 1048576 ? `${(v / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((v || 0) / 1024))} KB`);

// Documentos de un inmueble o de uno de sus contratos: escritura, contrato firmado,
// recibo del IBI, facturas de mejoras... Se guardan en el bucket S3 del despacho.
export const DocumentosInmueble = ({ inmuebleId, contratoId = null, documentos = [],
                                    tipos = [], tipoPorDefecto = 'otro', onCambio }) => {
  const [tipo, setTipo] = useState(tipoPorDefecto);
  const [descripcion, setDescripcion] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const inputRef = useRef(null);

  const lista = useMemo(() => (documentos || []).filter((d) => (
    contratoId ? d.contrato_id === contratoId : true
  )), [documentos, contratoId]);

  const etiqueta = (codigo) => tipos.find((t) => t.codigo === codigo)?.etiqueta || codigo;

  const subir = async (ficheros) => {
    if (!ficheros?.length) return;
    const datos = new FormData();
    datos.append('tipo', tipo || 'otro');
    if (descripcion) datos.append('descripcion', descripcion);
    if (contratoId) datos.append('contrato_id', String(contratoId));
    Array.from(ficheros).forEach((f) => datos.append('archivos', f));
    setSubiendo(true);
    try {
      const r = await inmueblesAPI.subirDocumentos(inmuebleId, datos);
      toast({ title: `${r.documentos?.length || 0} documento(s) guardado(s)` });
      setDescripcion('');
      if (inputRef.current) inputRef.current.value = '';
      onCambio?.();
    } catch (e) {
      toast({ title: 'No se ha podido subir', description: e?.response?.data?.detail,
              variant: 'destructive' });
    } finally {
      setSubiendo(false);
    }
  };

  const descargar = async (doc) => {
    try {
      const r = await inmueblesAPI.descargarDocumento(doc.id);
      const url = window.URL.createObjectURL(new Blob([r.data],
        { type: doc.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'No se ha podido descargar', description: e?.response?.data?.detail,
              variant: 'destructive' });
    }
  };

  const borrar = async (doc) => {
    if (!window.confirm(`¿Borrar "${doc.nombre_archivo}"?`)) return;
    try {
      await inmueblesAPI.borrarDocumento(doc.id);
      onCambio?.();
    } catch (e) {
      toast({ title: 'No se ha podido borrar', variant: 'destructive' });
    }
  };

  const cambiarTipo = async (doc, nuevo) => {
    try {
      await inmueblesAPI.actualizarDocumento(doc.id, { tipo: nuevo });
      onCambio?.();
    } catch (e) {
      toast({ title: 'No se ha podido cambiar el tipo', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3" data-testid={`documentos-inmueble-${inmuebleId}${contratoId ? `-contrato-${contratoId}` : ''}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-xs">Tipo de documento</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-9" data-testid="documento-tipo-trigger">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              {tipos.map((t) => (
                <SelectItem key={t.codigo} value={t.codigo}>{t.etiqueta}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Descripción (opcional)</Label>
          <Input className="h-9" value={descripcion} placeholder="p. ej. notaría Martí, 2019"
                 data-testid="documento-descripcion"
                 onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div>
          <input ref={inputRef} type="file" multiple className="hidden"
                 data-testid="documento-input-archivo"
                 onChange={(e) => subir(e.target.files)} />
          <Button className="w-full h-9" disabled={subiendo}
                  onClick={() => inputRef.current?.click()}
                  data-testid="documento-subir-btn">
            {subiendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <FileUp className="w-4 h-4 mr-1" />}
            {subiendo ? 'Subiendo…' : 'Añadir archivos'}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        PDF, imágenes, Word, Excel, CSV, XML, ZIP o correo. Hasta 25 MB por archivo y los que
        hagan falta. Se guardan en el almacenamiento del despacho, no en este ordenador.
      </p>

      {lista.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="documentos-vacio">
          Todavía no hay documentos guardados.
        </p>
      )}
      <div className="space-y-1">
        {lista.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-2 border rounded px-2 py-1.5"
               data-testid={`documento-${d.id}`}>
            <div className="min-w-0">
              <p className="text-xs font-medium truncate flex items-center gap-1">
                <Paperclip className="w-3 h-3 flex-shrink-0" /> {d.nombre_archivo}
              </p>
              <p className="text-[11px] text-slate-500">
                <Badge variant="secondary" className="mr-1 text-[10px]">{etiqueta(d.tipo)}</Badge>
                {kb(d.tamano)}
                {d.created_at ? ` · ${String(d.created_at).slice(0, 10)}` : ''}
                {d.contrato_id && !contratoId ? ' · del contrato' : ''}
                {d.descripcion ? ` · ${d.descripcion}` : ''}
                {d.almacenamiento === 'local' ? ' · pendiente de subir al bucket' : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Select value={d.tipo} onValueChange={(v) => cambiarTipo(d, v)}>
                <SelectTrigger className="h-7 w-[150px] text-xs"
                               data-testid={`documento-tipo-${d.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => (
                    <SelectItem key={t.codigo} value={t.codigo}>{t.etiqueta}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => descargar(d)}
                      data-testid={`documento-descargar-${d.id}`}>
                <Download className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => borrar(d)}
                      data-testid={`documento-borrar-${d.id}`}>
                <Trash2 className="w-3 h-3 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentosInmueble;
